/**
 * Capture-Modul für das "beforeinstallprompt"-Event (PWA-Installation).
 *
 * Chrome feuert das Event sehr früh – oft bevor React gemountet ist. Deshalb
 * registriert dieses Modul seine Listener bereits beim Import (Modulebene)
 * und muss als einer der ersten Imports in src/index.js geladen werden.
 * Interessierte Komponenten (InstallAppBanner) abonnieren Änderungen über
 * subscribeInstallPrompt().
 */

let deferredPrompt = null;
const subscribers = new Set();

const notifySubscribers = () => {
  subscribers.forEach((cb) => {
    try {
      cb(deferredPrompt);
    } catch (e) {
      // Ein fehlerhafter Subscriber darf die anderen nicht blockieren.
    }
  });
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Browser-eigene Mini-Infobar unterdrücken; wir zeigen einen eigenen Banner.
    event.preventDefault();
    deferredPrompt = event;
    notifySubscribers();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notifySubscribers();
  });
}

/**
 * Abonniert Änderungen am gemerkten Install-Prompt.
 * Der Callback wird sofort mit dem aktuellen Stand aufgerufen.
 *
 * @param {(prompt: Event|null) => void} callback
 * @returns {() => void} Unsubscribe-Funktion
 */
export const subscribeInstallPrompt = (callback) => {
  subscribers.add(callback);
  callback(deferredPrompt);
  return () => subscribers.delete(callback);
};

/**
 * Löst den gemerkten Browser-Install-Dialog aus.
 *
 * @returns {Promise<'accepted'|'dismissed'|null>} Ergebnis der Nutzerentscheidung,
 *   oder null wenn kein Prompt verfügbar war.
 */
export const promptInstall = async () => {
  if (!deferredPrompt) {
    return null;
  }
  // Vor dem await ausnullen: Ein beforeinstallprompt-Event ist nur einmal
  // verwendbar; ein Doppelklick darf prompt() nicht zweimal aufrufen
  // (InvalidStateError).
  const prompt = deferredPrompt;
  deferredPrompt = null;
  notifySubscribers();
  try {
    prompt.prompt();
    const choice = await prompt.userChoice;
    return choice && choice.outcome ? choice.outcome : null;
  } catch (e) {
    return null;
  }
};
